import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";

function trapTabKey(event, root) {
  if (event.key !== "Tab" || !root) return;
  const nodes = Array.from(
    root.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((node) => node.offsetParent !== null || node === document.activeElement);
  if (nodes.length === 0) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Accessible delete confirmation outside media-card geometry.
 * Local unuploaded cards never reach HTTP from here — callers decide.
 */
function PropertyMediaDeleteDialog({
  open,
  title,
  message,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const timer = window.setTimeout(() => {
      cancelRef.current?.focus();
    }, 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (busy) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        onCancel?.();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [open, busy, onCancel]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="property-media-delete"
      role="presentation"
      data-testid="property-media-delete-dialog"
    >
      <div
        className="property-media-delete__backdrop"
        aria-hidden="true"
        onClick={() => {
          if (!busy) onCancel?.();
        }}
      />
      <div
        ref={dialogRef}
        className="property-media-delete__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-media-delete-title"
        aria-describedby="property-media-delete-message"
      >
        <h2 id="property-media-delete-title">{title}</h2>
        <p id="property-media-delete-message">{message}</p>
        <div className="property-media-delete__actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Скасувати
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            Видалити
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default PropertyMediaDeleteDialog;
